<script setup>
  import {
    computed,
    ref,
    watch,
    watchPostEffect,
  } from 'vue';

  import { storeToRefs } from 'pinia';
  import deepmerge from 'deepmerge';

  import invalidateCPF, { CPF_FORMAT } from '@validators/cpf';
  import { CNPJ_FORMAT } from '@validators/cnpj';

  import patternize, { depatternize } from '@formatters/patternize';

  import axios from '@js/clients/axios';
  import { STATES } from '@js/constants/options';

  import { BANK_UNION, BANK_EXCLUSION, BANK_FINDER } from '@js/constants/banks';

  import useAuthStore from '@/stores/auth';

  import filled from '@/components/Form/javascripts/filled';
  import { sort } from '@/components/Form/javascripts/optionize';
  import { settings as buttonSettings } from '@/components/Form/composables/buttoner';

  import routers from '@/routers';

  import FormCore from '@/components/Form/Core.vue';

  import CredereButton from '@/components/Credere/Button.vue';
  import CredereTag from '@/components/Credere/Tag.vue';
  import CredereTags from '@/components/Credere/Tags.vue';

  import {
    user as userNormalizer,
    vehicle as vehicleNormalizer,
  } from './javascripts/normalizers';

  import CALLBACKS, { leadStorageSet, leadStorageGet } from './javascripts/callbacks';
  import COMBINE from './javascripts/combine';
  import CONSTANTS from './javascripts/constants';
  import SIZES from './javascripts/sizes';
  import UNGROUP from './javascripts/ungroup';
  import SORTS from './javascripts/sorts';
  import TIPS from './javascripts/tips';

  import parser from './javascripts/parser';
  import normalizer from './javascripts/normalizer';

  import SCHEMA, { POSSIBLE_STEPS, LEAD_REQUIRED } from './SCHEMA';

  const authStore = useAuthStore();
  const { scope, user } = storeToRefs(authStore);
  const EMBEDDED = scope.value.includes('embedded');
  const role = computed(() => user?.value?.role?.identifier);

  const refCore = ref();

  const globalSettings = window.CREDERE.settings || {};

  const SETTINGS = normalizer({
    _proposal: globalSettings.simulations?.form?._proposal || {},
    simulation: globalSettings.simulations?.form || {},
    lead: globalSettings.leads?.form || {},
  });

  SETTINGS.simulation.vehicle = SETTINGS.simulation.vehicle || {};

  const ACCEPTING_POLICY = false
    || SETTINGS.simulation._enable_privacy_policy_checkbox
    || SETTINGS.lead._enable_privacy_policy_checkbox;

  const acceptPolicy = ref(!ACCEPTING_POLICY);

  const CODES = BANK_UNION(SETTINGS.simulation.bank_codes || SETTINGS.lead.bank_codes);
  const DEFAULT_DYNAMIC_BANKS = !!(false
    || SETTINGS.lead._enable_dynamic_banks
    || SETTINGS.lead._dynamic_banks
    || SETTINGS.simulation._enable_dynamic_banks
    || SETTINGS.simulation._dynamic_banks
  );
  const DYNAMIC_BANKS = ref(DEFAULT_DYNAMIC_BANKS);

  if (EMBEDDED) {
    delete SETTINGS.simulation?._condition?._enable_custom_settings;
  }

  if (Array.isArray(SETTINGS.simulation._condition?.installments)) {
    SETTINGS.simulation._condition.installments = SETTINGS.simulation._condition.installments.join(',');
  }

  function LEGACY(parent, fields) {
    if (parent[fields[0]] && !parent[fields[1]]) {
      parent[fields[1]] = parent[fields[0]];

      delete parent[fields[0]];
    }

    if (parent[`_${fields[0]}`] && !parent[`_${fields[1]}`]) {
      parent[`_${fields[1]}`] = parent[`_${fields[0]}`];

      delete parent[`_${fields[0]}`];
    }
  }

  LEGACY(SETTINGS.simulation.vehicle, ['vehicle_molicar_code', 'credere_vehicle_model_id']);
  LEGACY(SETTINGS.simulation, ['seller_cpf', 'credere_seller_id']);

  const PROPOSAL_ID = SETTINGS._proposal.id;
  const PROPOSAL_ATTEMPT_ID = SETTINGS._proposal?.proposal_attempt_id
    || SETTINGS._proposal?.proposal_attempt?.id;

  let SIMULATION_UUID = SETTINGS.simulation.uuid;
  let SIMULATION_STORE_ID = SETTINGS.simulation.store_id;

  let SELLER_ID = SETTINGS.simulation.seller_id
    || SETTINGS.simulation.seller?.id
    || SETTINGS.simulation.credere_seller_id;

  const USED_VEHICLE_ID = SETTINGS.simulation.vehicle?.credere_vehicle_id;

  const ENABLE_VEHICLE_PRICES = SETTINGS.simulation.vehicle?._enable_price_commercial_control;
  const VEHICLE_MODEL_ID = SETTINGS.simulation.vehicle?.credere_vehicle_model_id;
  const VEHICLE_PRICE = SETTINGS.simulation.vehicle?.asset_value;

  let VEHICLE_DEFAULT_PRICE;
  let VEHICLE_MINIMUM_PRICE;

  if (USED_VEHICLE_ID) {
    delete SETTINGS.simulation.vehicle?.name;
    delete SETTINGS.simulation.vehicle?.vehicle_molicar_code;
    delete SETTINGS.simulation.vehicle?.credere_vehicle_model_id;
    delete SETTINGS.simulation.vehicle?.model_year;
    delete SETTINGS.simulation.vehicle?.manufacture_year;
    delete SETTINGS.simulation.vehicle?.zero_km;
  }

  const primaryButton = buttonSettings(SETTINGS.simulation._buttons, 'primary', 'Fazer simulação');

  const domains = ref({});
  const loaded = ref(false);

  function schemaInitializer() {
    if (ENABLE_VEHICLE_PRICES) {
      SCHEMA
        .simulation
        .fields
        .vehicle
        .fields
        .asset_value
        .validations
        .find((i) => i.type === 'greater_than')
        .count = VEHICLE_MINIMUM_PRICE;
    }

    SCHEMA.lead.fields.cpf_cnpj.rules = LEAD_REQUIRED(CODES);

    if ([true, 'true'].includes(SETTINGS.lead._enable_cnpj)) {
      SCHEMA.lead.fields.cpf_cnpj.mask = {
        type: 'patternize',
        format: CPF_FORMAT,
        bigFormat: CNPJ_FORMAT,
      };

      SCHEMA.lead.fields.cpf_cnpj.validations = [{
        type: 'cpfOrCnpj',
      }, {
        type: 'blank',
      }];
    }

    return SCHEMA;
  }

  function leadSuccess(response) {
    return response.data.data;
  }

  function simulationSuccess(response) {
    return response.data.data;
  }

  const singulars = ['lead', 'simulation'];
  const identifiers = ['cpf_cnpj', 'uuid'];
  const endpoints = ['/banks_api/leads', '/banks_api/simulations'];
  const chains = [leadSuccess, simulationSuccess];

  function success(hierarchy, action, index, parsed /* , response */) {
    if (singulars[index] === 'lead') {
      leadStorageSet(parsed.lead.cpf_cnpj, parsed);
    }

    if (!endpoints[index + 1]) {
      routers.app.push({ name: 'home-blank' });
    }
  }

  function failure(hierarchy, action, index, parsed, error) {
    /* eslint-disable no-console */
    console.log('SIMULATIONS/CREATE FAILURE');
    console.error(error);
    /* eslint-enable no-console */
  }

  function chain(/* hierarchy, action, index, parsed, chain */) {}

  let entity;
  const READONLY = {};

  function preloadSuccess(responses) {
    if (responses.some((i) => i?.status !== 200 && i?.statusText === 'CUSTOM')) {
      preloadFailure(new Error('Não foi possível se conectar à Banks-API.'));

      return;
    }

    Object
      .keys(responses[0].data.data)
      .forEach((i) => {
        domains.value[i.replace(/-/g, '_')] = responses[0].data.data[i];
      });

    domains.value.states = STATES;
    domains.value.stores = responses[1].data.stores;
    domains.value.grouped_vehicle_model = responses[2].data.vehicle_models.map(vehicleNormalizer);
    domains.value.seller = responses[3].data.users.map(userNormalizer);

    let vehicle = responses[6]?.data.vehicle;
    const simulationVehicle = responses[4]?.data.data.vehicle.credere_vehicle?.id;

    VEHICLE_DEFAULT_PRICE = responses[7]?.data.vehicle_prices?.[0]?.default_price_cents || 0;
    VEHICLE_MINIMUM_PRICE = responses[7]?.data.vehicle_prices?.[0]?.min_price_cents || 0;

    if (!vehicle && simulationVehicle) {
      SETTINGS.simulation.vehicle.credere_vehicle_id = simulationVehicle;

      vehicle = responses[4]?.data.data.vehicle;
    }

    if (vehicle) {
      READONLY.vehicle = READONLY.vehicle || {};

      const readonly = (name, value) => {
        SETTINGS.simulation.vehicle[name] = filled(SETTINGS.simulation.vehicle[name])
          ? SETTINGS.simulation.vehicle[name]
          : value;

        const _visibility = SETTINGS.simulation.vehicle[`_${name}`]?.visibility;

        READONLY.vehicle[`_${name}`] = deepmerge(READONLY.vehicle[`_${name}`], {
          readonly: true,
          visibility: filled(_visibility) ? _visibility : true,
        });
      };

      readonly('name', vehicleNormalizer(vehicle.vehicle_model).value);
      readonly('model_year', vehicle.model_year);
      readonly('manufacture_year', vehicle.manufacture_year);
      readonly('credere_vehicle_model_id', vehicle.vehicle_model.credere_id || vehicle.vehicle_model.id);
      readonly('zero_km', [true, 'true'].includes(vehicle.zero_km));

      const _readonly = SETTINGS.simulation.vehicle?._asset_value?.readonly;
      SETTINGS.simulation.vehicle.asset_value = vehicle.price_cents;
      SETTINGS.simulation.vehicle._asset_value = SETTINGS.simulation.vehicle._asset_value || {};
      SETTINGS.simulation.vehicle._asset_value.readonly = filled(_readonly) ? _readonly : true;

      if (!SIMULATION_STORE_ID) {
        SETTINGS.simulation.store_id = vehicle.store?.id || vehicle.store_id;
        SETTINGS.simulation._store_id = deepmerge(SETTINGS.simulation._store_id, {
          visibility: true,
        });
      }
    } else if (VEHICLE_DEFAULT_PRICE) {
      SETTINGS.simulation.vehicle.asset_value = VEHICLE_DEFAULT_PRICE;
      SETTINGS.simulation.vehicle._asset_value = SETTINGS.simulation.vehicle._asset_value || {};

      const _visibile = SETTINGS.simulation.vehicle._asset_value.visibility;
      SETTINGS.simulation.vehicle._asset_value.visibility = filled(_visibile) ? _visibile : true;
    }

    if (VEHICLE_MODEL_ID && !SETTINGS.simulation.vehicle?.name) {
      SETTINGS.simulation.vehicle.name = 'Selecionado via configuração';
    }

    const LEAD_ENTITY = responses[4]?.data?.data?.lead || responses[5]?.data?.data || {};

    const ENTITIES = {
      simulation: responses[4]?.data?.data || {},
      lead: EMBEDDED ? deepmerge(leadStorageGet(LEAD_ENTITY.cpf_cnpj), LEAD_ENTITY) : LEAD_ENTITY,
    };

    if (ENTITIES.simulation.uuid) {
      ENTITIES.simulation._condition = {
        down_payment: ENTITIES.simulation
          .conditions?.find((i) => !i.suggestion_reason)?.down_payment || 0,
      };
    }

    delete ENTITIES.simulation.id;

    if (READONLY.simulation) {
      SETTINGS.simulation = deepmerge(SETTINGS.simulation, READONLY.simulation);
    }

    if (READONLY.vehicle) {
      SETTINGS.simulation.vehicle = deepmerge(SETTINGS.simulation.vehicle, READONLY.vehicle);
    }

    if (READONLY.lead) {
      SETTINGS.lead = deepmerge(SETTINGS.lead, READONLY.lead);
    }

    let seller;
    // eslint-disable-next-line no-useless-escape
    if (filled(SELLER_ID) && !invalidateCPF(SELLER_ID)) {
      const cpf = patternize(SELLER_ID, { format: CPF_FORMAT });

      seller = domains.value.seller.find((i) => patternize(i.cpf, { format: CPF_FORMAT }) === cpf);

      if (!seller) {
        const secID = cpf.split('').map((i) => (/\d/.test(i) ? parseInt(i, 10) : 0)).reduce((_, i) => _ + i, 0);

        seller = domains.value.seller.find((i) => i.secid === secID);
      }

      if (!seller && SETTINGS.simulation._credere_seller_id?.weak) {
        SELLER_ID = null;

        delete SETTINGS.simulation.credere_seller_id;
        delete SETTINGS.simulation._credere_seller_id;
      }
    } else if (SELLER_ID) {
      const sellerID = parseInt(SELLER_ID, 10);
      seller = domains.value.seller.find((i) => i.id === sellerID);
    }

    if (seller) {
      SELLER_ID = seller.id;
      SETTINGS.simulation.credere_seller_id = seller.id;
    }

    entity = ENTITIES;
    loaded.value = true;

    setTimeout(() => {
      const store = refCore.value.planter.values.value['simulation[store_id]'];

      const name = refCore.value.planter.values.value['simulation[vehicle][name]'];
      const year = refCore.value.planter.values.value['simulation[vehicle][model_year]'];
      const vehicleID = refCore.value.planter.values.value['simulation[vehicle][credere_vehicle_model_id]'];

      const cpf = refCore.value.planter.values.value['lead[cpf_cnpj]'];

      if (filled(name)) {
        refCore.value.planter.values.value['simulation[vehicle][name]'] = { blur: true };
      }

      if (filled(year) && filled(vehicleID)) {
        refCore.value.planter.values.value['simulation[vehicle][model_year]'] = { blur: true };
      }

      if (filled(cpf)) {
        refCore.value.planter.values.value['lead[cpf_cnpj]'] = { blur: true };
      } else if (filled(store)) {
        refCore.value.planter.values.value['simulation[store_id]'] = { blur: true };
      }
    }, 10);
  }

  function preloadFailure(error) {
    /* eslint-disable no-console */
    console.log('DOMAINS/LIST FAILURE');
    console.error(error);
    /* eslint-enable no-console */

    // eslint-disable-next-line no-alert
    window.alert('Ocorreu um erro ao tentar carregar as configurações do formulário. Por favor, tente novamente.');
  }

  function preload(responses) {
    const _proposal = responses?.[0]?.data?.proposal;
    const _attempts = responses?.[1]?.data?.proposal_attempts;

    if (
      _proposal
      && (!PROPOSAL_ATTEMPT_ID || _attempts.length > 1 || _attempts[0].external_proposal_uuid)
    ) {
      const readonly = (_readonly, _settings, names) => {
        names.forEach((name) => {
          const _visibility = _settings?.[`_${name}`]?.visibility;

          _readonly[`_${name}`] = deepmerge(_readonly[`_${name}`], {
            readonly: true,
            visibility: filled(_visibility) ? _visibility : true,
          });
        });
      };

      READONLY.simulation = {};
      readonly(
        READONLY.simulation,
        SETTINGS.simulation,
        [
          'store_id',
          'credere_seller_id',
          'commercial',
        ],
      );

      READONLY.vehicle = {};
      readonly(
        READONLY.vehicle,
        SETTINGS.simulation.vehicle,
        [
          'name',
          'credere_vehicle_model_id',
          'manufacture_year',
          'model_year',
          'zero_km',
          'licensing_uf',
          'licensing_city',
        ],
      );

      READONLY.lead = {};
      readonly(
        READONLY.lead,
        SETTINGS.lead,
        [
          'cpf_cnpj',
        ],
      );
    }

    if (_proposal?.external_simulation_uuid && _proposal?.store?.id) {
      SIMULATION_UUID = _proposal?.external_simulation_uuid;
      SIMULATION_STORE_ID = _proposal?.store?.id;
    }

    const HEADERS = { headers: { 'store-id': SIMULATION_STORE_ID } };
    const LEAD_CPF = !SIMULATION_UUID && SIMULATION_STORE_ID && SETTINGS.lead.lead?.cpf_cnpj;

    if (_proposal?.id) {
      SELLER_ID = _proposal?.seller?.id;
    }

    const PRICE_PARAMS = {
      params: {
        store_id: SIMULATION_STORE_ID,
        [/-/.test(VEHICLE_MODEL_ID) ? 'molicar_code' : 'vehicle_model_id']: VEHICLE_MODEL_ID,
      },
    };

    Promise
      .all([
        axios.get('/banks_api/domains'),
        axios.get('/stores', { params: { per_page: 99999 } }),
        axios.get('/vehicle_models/grouped', { params: { per_page: 99999 } }),
        axios.get('/users/proposals_filter_list', { params: { per_page: 99999 } }),
        SIMULATION_UUID && SIMULATION_STORE_ID ? axios.get(`/banks_api/simulations/${SIMULATION_UUID}`, HEADERS) : Promise.resolve(),
        !(SIMULATION_UUID && SIMULATION_STORE_ID) && LEAD_CPF ? axios.get(`/banks_api/leads/${depatternize(LEAD_CPF)}`, HEADERS) : Promise.resolve(),
        USED_VEHICLE_ID ? axios.get(`/vehicles/${USED_VEHICLE_ID}`) : Promise.resolve(),
        (ENABLE_VEHICLE_PRICES && SIMULATION_STORE_ID && VEHICLE_MODEL_ID && !VEHICLE_PRICE && !USED_VEHICLE_ID && !PROPOSAL_ID && !SIMULATION_UUID) ? axios.get('/vehicle_prices', PRICE_PARAMS) : Promise.resolve(),
      ])
      .then(preloadSuccess)
      .catch(preloadFailure);
  }

  // ########################################################################################## //
  // ################################## LEAD DYNAMIC FIELDS ################################### //
  // ########################################################################################## //

  const _currentStep = ref(DYNAMIC_BANKS.value ? 0 : POSSIBLE_STEPS.length - 1);
  const currentStep = computed({
    get() {
      return Math.max(
        // eslint-disable-next-line no-use-before-define
        Math.min(_currentStep.value, currentSteps.value.length - 1),
        0,
      );
    },
    set(value) {
      _currentStep.value = value;
    },
  });
  const currentSteps = computed(() => {
    if (!refCore.value) {
      return [CODES];
    }

    const steps = POSSIBLE_STEPS.map(() => ([]));

    for (let i = steps.length - 1; i >= 0; i -= 1) {
      POSSIBLE_STEPS[i]
        .forEach((field) => Object
          .keys(refCore.value.planter.leaves.value[field].rules)
          .filter((code) => steps.slice(i).every((s) => !s.includes(code)))
          .forEach((code) => steps[i].push(code)));
    }

    return steps
      .filter((i) => i.length)
      .map((i) => i.sort((a, b) => sort({
        label: BANK_FINDER(a).name,
      }, {
        label: BANK_FINDER(b).name,
      })));
  });

  const currentBanks = computed(() => {
    const banks = currentSteps.value.slice(0, currentStep.value + 1).flat();
    const storeID = parseInt(refCore.value?.planter.values.value['simulation[store_id]'], 10);
    const store = storeID && domains.value?.stores?.find((i) => i.id === storeID);
    const fallback = store?._banks ? BANK_EXCLUSION(store._banks, CODES) : CODES;

    return banks.length ? BANK_EXCLUSION(banks, fallback) : fallback;
  });

  const nextStep = computed(() => currentSteps.value?.[currentStep.value + 1]);
  const prevStep = computed(() => currentSteps.value?.[currentStep.value - 1]);

  function banksAdd() {
    if (nextStep.value) {
      currentStep.value += 1;
    }
  }

  function banksRemove() {
    if (prevStep.value) {
      currentStep.value -= 1;
    }
  }

  const currentDynamic = computed(() => {
    const a = currentSteps.value[currentStep.value].slice().sort().join('-');
    const b = CODES.slice().sort().join('-');

    return a !== b;
  });

  watchPostEffect(() => {
    const storeID = parseInt(refCore.value?.planter.values.value['simulation[store_id]'], 10);
    const store = domains.value?.stores?.find((i) => i.id === storeID);

    DYNAMIC_BANKS.value = ((
      store?.limit_simulation_bank_visibility
      && CONSTANTS.ROLES.LIMIT_SIMULATION_BANK_VISIBILITY.includes(role.value)
    ) || (
      store?.show_full_leads_form_to_sellers
      && CONSTANTS.ROLES.SHOW_FULL_LEADS_FORM_TO_SELLERS.includes(role.value)
    )) ? false : DEFAULT_DYNAMIC_BANKS;
  });

  const currentLoading = ref();
  watch(currentLoading, () => {
    if (DYNAMIC_BANKS.value) {
      currentStep.value = Math.max.apply(null, Object
        .keys(refCore.value?.planter.leaves.value || {})
        .filter((field) => /^lead\[/.test(field) && refCore.value?.planter.values.value[field])
        .map((field) => POSSIBLE_STEPS.findIndex((step) => step.includes(field))).concat(0));
    } else {
      currentStep.value = POSSIBLE_STEPS.length - 1;
    }
  });
  watchPostEffect(() => {
    currentLoading.value = refCore.value?.planter.loadings.value['lead[cpf_cnpj]'];
  });

  if (PROPOSAL_ID) {
    Promise
      .all([
        axios.get(`/proposals/${PROPOSAL_ID}`),
        axios.get(`/proposals/${PROPOSAL_ID}/proposal_attempts`),
      ])
      .then(preload)
      .catch(preload);
  } else {
    preload();
  }
</script>

<template>
  <form-core
    v-if="loaded"

    ref="refCore"

    class="simulations-form"

    :banks="currentBanks"
    :banks-inclusion="CODES"

    :initializer="schemaInitializer"
    :entity="entity"

    :settings="SETTINGS"

    :callbacks="CALLBACKS"
    :combine="COMBINE"
    :constants="CONSTANTS"
    :sizes="SIZES"
    :ungroup="UNGROUP"
    :sorts="SORTS"
    :tips="TIPS"

    :domains="domains"

    :parser="parser"
    :normalizer="normalizer"

    :endpoint="endpoints"
    :singular="singulars"
    :identifier="identifiers"
    :chain="chains"

    :primary-button="primaryButton"

    :disabled="!acceptPolicy"

    @success="success"
    @failure="failure"
    @chain="chain"
  >
    <template #header:lead>
      <section
        v-if="DYNAMIC_BANKS && currentDynamic"
        class="simulations-form-banks"
      >
        <div class="simulations-form-banks-buttons">
          <credere-button
            :disabled="!prevStep || currentLoading"
            icon="minus"
            size="small"
            @click.prevent="banksRemove"
          >
            Remover bancos
          </credere-button>

          <credere-button
            :disabled="!nextStep || currentLoading"
            icon="plus"
            size="small"
            @click.prevent="banksAdd"
          >
            Adicionar bancos
          </credere-button>
        </div>

        <p class="simulations-form-banks-paragraph">
          Estes serão os bancos simulados:
        </p>

        <credere-tags>
          <template
            v-for="(step, index) in currentSteps"
            :key="step.join(',')"
          >
            <credere-tag
              v-for="code in step"
              :key="code"
              :disabled="currentStep < index"
            >
              {{ BANK_FINDER(code).name }}
            </credere-tag>
          </template>
        </credere-tags>
      </section>
    </template>

    <template
      #buttons
      v-if="ACCEPTING_POLICY"
    >
      <section class="simulations-form-accept">
        <input
          class="simulations-form-accept-input"
          name="simulation[_accept]" type="checkbox"
          id="simulation[_accept]"
          v-model="acceptPolicy"
        />

        <label
          class="simulations-form-accept-label"
          for="simulation[_accept]"
        >
          Li e concordo com os
          <a
            class="simulations-form-accept-link"
            href="https://meucredere.com.br/termos_de_uso.html"
            target="_blank"
          >Termos e Condições</a>
          e
          <a
            class="simulations-form-accept-link"
            href="https://meucredere.com.br/politica_de_privacidade.html"
            target="_blank"
          >Política de Privacidade</a>
        </label>
      </section>
    </template>
  </form-core>
</template>

<style lang="scss">
  .simulations-form-banks {
    --background-color: var(--token-color-neutral-lighter);
    --border-radius: var(--token-border-radius-small);
    --color: var(--token-color-constant-dark);
    --margin: var(--token-spacing-giga) 0;
    --padding: var(--token-spacing-xl);

    @container (max-width: 768px) {
      --padding: var(--token-spacing-xs);
    }
  }

  .simulations-form-banks-buttons {
    --gap: 1em;

    align-items: center;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-start;
  }

  .simulations-form-banks-paragraph {
    --margin: var(--token-spacing-xl) 0 var(--token-spacing-micro);

    @container (max-width: 768px) {
      --font-size: var(--token-font-size-s);
    }
  }

  .simulations-form-accept {
    --gap: var(--token-spacing-micro);

    align-items: center;
    align-self: center;
    display: flex;
    flex-direction: row;
    flex: 1;
    justify-content: flex-start;

    @container (max-width: 768px) {
      --font-size: var(--token-font-size-s);
    }
  }

  .simulations-form-accept-link {
    --color: var(--token-color-alert-information);
  }
</style>
